import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { ProductService } from '../Services/product';
import { FabricService } from '../Services/fabric';
import { ColorService } from '../Services/color';
import { BrandService } from '../Services/brand';
import { StateService } from '../Services/state';
import { CategoryService } from '../Services/category';
import { SizeService } from '../Services/size';
import { AlertService } from '../Services/alert';
import { AuthService } from '../Services/auth';
import { LanguageService } from '../Services/lang';
import { Product, ProductImage } from '../Models/product';
import { Color } from '../Models/color';
import { Category } from '../Models/category';
import { Fabric } from '../Models/fabric';
import { Brand } from '../Models/brand'
import { State } from '../Models/state';
import { Size } from '../Models/size';
import { User } from '../Models/user';


@Component({
  templateUrl: '../Templates/product.create.html',
  styleUrls: ['../Styles/product.create.scss']
})


/**
 * This component takes care of all handling around creation of
 * products. It implements a step through procedure for creating and
 * uploading products.
 */
export class ProductCreateComponent {

  model: Product;
  colors: Color[];
  categories: Category[];
  fabrics: Fabric[];
  brands: Brand[];
  states: State[];
  sizes: Size[];
  user: User;

  // Current step, and number of steps total
  private step: number = 0;
  private steps: Array<string>;

  public numberOfExtraImages: number = 4; // 4 optional extra images
  public submitted: boolean;

  public mode = "create";

  constructor(protected productService: ProductService,
              private colorService: ColorService,
              private brandService: BrandService,
              private stateService: StateService,
              private fabricService: FabricService,
              private categoryService: CategoryService,
              private sizeService: SizeService,
              protected alertService: AlertService,
              private auth: AuthService,
              private lang: LanguageService,
              protected route: ActivatedRoute,
              protected router: Router) {

    this.model = new Product();
    this.user = null;
    this.steps = ["images", "details", "price", "check", "register"];

    // create empty set of images, so the imagecomponent can use it
    for (let i = 0; i < this.numberOfExtraImages; ++i) {
      this.model.images.push({'image': new ProductImage()});
    }

    this.model.frontimage = new ProductImage();
    this.model.backimage = new ProductImage();
    this.model.detailimage = new ProductImage();
  }

  onSubmit() {

    this.submitted = true;

    // clean up possible empty images
    var images: Array<any> = [];

    for (let i = 0; i < this.model.images.length; i++) {
      if (this.model.images[i].image.value) {
        images.push(this.model.images[i]);
      }
    }

    this.model.images = images;

    if (this.model.size == -1) {
      this.model.size = null;
    }

    this.handleSubmit();

  }

  protected handleSubmit() {

    this.productService.create(this.model).subscribe(
      data => {
        this.alertService.success("Created new product");
        this.router.navigate(['pages', 'product_created']);
      },
      error => {
        if (error.name == "TimeoutError") {
          this.alertService.error("Your connection is slow! Please be patient");
        } else {
          this.alertService.error("Error saving product!");
          this.submitted = false;  // allow user to fix the input and resubmit
        }
      }
    );
  }

  /**
   * The image group is a bit different. File inputs don't do the
   * 'valid' thing.
   */
  validateImageGroup(): boolean {

    if (this.model.detailimage.value && this.model.backimage.value && this.model.frontimage.value) {
      return true;
    } else {
      return false;
    }
  }

  nextStep() {
    this.step++;
    if (this.step > this.steps.length) {
      this.step = this.steps.length;
    }
  }

  previousStep() {
    this.step--;
    if (this.step < 0) {
      this.step = 0;
    }
  }

  canStepForward() {

    return this.step < this.steps.length - 1;
  }

  canStepBackward() {
    return this.step > 0;
  }

  /**
   * Only registered users can actually create a product, so if the
   * creation step is nigh, enforce registration.
   */
  canCreate(): boolean {

    return (this.user && !this.user.is_tmp && this.step >= 1);
  }

  isStep(stepname: string) {
    return this.steps.indexOf(stepname) == this.step;
  }

  private loadColors(): void {
    this.colorService.list()
      .subscribe(data => this.colors = data['results'])
  }

  private loadFabrics(): void {
    this.fabricService.list()
      .subscribe(data => this.fabrics = data['results'])
  }

  private loadBrands(): void {
    this.brandService.list()
      .subscribe(data => this.brands = data['results'])
  }

  private loadStates(): void {
    this.stateService.list()
      .subscribe(data => this.states = data['results'])
  }

  protected loadSizes(): void {

    if (!this.model.category) {
      return;
    }

    this.sizeService.list([this.model.category])
      .subscribe(data => this.sizes = data['results'])
  }

  private loadCategories(): void {
    this.categoryService.list()
      .subscribe(data => this.categories = data['results'])
  }

  /**
   * The category changed, so we need to reload sizes, and empty
   * existing value
   */
  protected categoryChanged(): void {

    this.model.size = undefined;
    this.model.custom_size = "";
    this.loadSizes();
  }

  protected sizeChanged(): void {

    if (this.model.size != -1 && this.model.custom_size) {
      this.model.custom_size = "";
    }
  }

  ngOnInit() {
    this.loadBrands();
    this.loadColors();
    this.loadFabrics();
    this.loadStates();
    this.loadCategories();

    this.auth.getUser().subscribe(
      user => {

        this.user = user;

        if (user && !user.is_tmp) {
          this.steps.pop();

          if (this.step >= this.steps.length - 1) {
            this.step = this.steps.length - 1
          }

        } else if (this.steps.length == 4) {
          // TODO: other check here, to make the steps more easy
          // configurable.
          this.steps.push('register');
        }
      }
    );
  }

  /**
   * Keep track of changes in vocab values and set value on model.
   */
  setValue(val: number, field: string, vocab: any[]) {

    for (var i = 0; i < vocab.length; i++) {
      if (vocab[i].id == val) {
        this.model[field] = vocab[i];
        break;
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/Components/product.create.ts